import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  CircleAlert,
  FileSpreadsheet,
  FlaskConical,
  Globe,
  Mail,
  Megaphone,
  MessageCircle,
  PenLine,
  Search,
  Send,
  type LucideIcon,
} from 'lucide-react';
import {
  CONNECTION_STATUS_LABELS,
  LEAD_STATUS_LABELS,
  type ConnectionStatus,
  type LeadSource,
  type LeadStatus,
  type MatchConfidence,
} from '@onesource/shared';
import { cn } from '@/lib/utils';
import { Badge } from './ui/display';
import { Button } from './ui/button';
import { Tooltip } from './ui/overlays';

// ---------- Brand glyphs (simplified, monochrome-friendly) ----------
function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="11" fill="#1877F2" />
      <path d="M13.4 19v-5.6h1.9l.3-2.2h-2.2V9.8c0-.6.2-1.1 1.1-1.1h1.2v-2a15 15 0 0 0-1.7-.1c-1.7 0-2.9 1-2.9 3v1.6H9.2v2.2h1.9V19h2.3Z" fill="#fff" />
    </svg>
  );
}
function MessengerGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <defs>
        <linearGradient id="msg-g" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#0866FF" />
          <stop offset="1" stopColor="#A033FF" />
        </linearGradient>
      </defs>
      <path d="M12 2C6.4 2 2 6.1 2 11.6c0 2.9 1.2 5.4 3.1 7.1v3.3l3-1.7c1.2.3 2.5.5 3.9.5 5.6 0 10-4.1 10-9.6S17.6 2 12 2Z" fill="url(#msg-g)" />
      <path d="m6.5 14.3 3-4.7 2.7 2.1 2.9-2.1-3 4.7-2.7-2.1-2.9 2.1Z" fill="#fff" />
    </svg>
  );
}
function InstagramGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <defs>
        <linearGradient id="ig-g" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#FEDA75" />
          <stop offset=".35" stopColor="#FA7E1E" />
          <stop offset=".65" stopColor="#D62976" />
          <stop offset="1" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig-g)" />
      <circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="17.2" cy="6.8" r="1.2" fill="#fff" />
    </svg>
  );
}

const LUCIDE: Partial<Record<LeadSource, { icon: LucideIcon; color: string }>> = {
  website_form: { icon: Globe, color: 'text-sky-600 dark:text-sky-400' },
  email: { icon: Mail, color: 'text-amber-600 dark:text-amber-400' },
  meta_lead_ads: { icon: Megaphone, color: 'text-blue-600' },
  google_lead_forms: { icon: Search, color: 'text-emerald-600' },
  whatsapp: { icon: MessageCircle, color: 'text-green-600' },
  telegram: { icon: Send, color: 'text-sky-500' },
  reddit: { icon: MessageCircle, color: 'text-orange-600' },
  manual: { icon: PenLine, color: 'text-muted' },
  csv_import: { icon: FileSpreadsheet, color: 'text-muted' },
};

export function SourceIcon({ source, className = 'size-4' }: { source: LeadSource; className?: string }) {
  if (source === 'facebook_messenger') return <MessengerGlyph className={className} />;
  if (source === 'instagram') return <InstagramGlyph className={className} />;
  const def = LUCIDE[source];
  const Icon = def?.icon ?? Globe;
  return <Icon className={cn(className, def?.color)} aria-hidden />;
}

export { FacebookGlyph, InstagramGlyph, MessengerGlyph };

// ---------- Status badges ----------
const LEAD_TONE: Record<LeadStatus, 'accent' | 'success' | 'neutral' | 'warning'> = {
  new: 'accent',
  replied: 'success',
  duplicate: 'neutral',
  needs_attention: 'warning',
};
export function LeadStatusBadge({ status }: { status: LeadStatus }) {
  return <Badge tone={LEAD_TONE[status]}>{LEAD_STATUS_LABELS[status]}</Badge>;
}

const CONNECTION_TONE: Record<ConnectionStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'accent'> = {
  connected: 'success',
  syncing: 'accent',
  setup_required: 'warning',
  permission_required: 'warning',
  degraded: 'warning',
  expired: 'danger',
  disconnected: 'neutral',
  unsupported: 'neutral',
};
export function ConnectionStatusBadge({ status }: { status: ConnectionStatus }) {
  return (
    <Badge tone={CONNECTION_TONE[status]}>
      <span className={cn('size-1.5 rounded-full bg-current', status === 'syncing' && 'animate-pulse')} />
      {CONNECTION_STATUS_LABELS[status]}
    </Badge>
  );
}

export function MatchBadge({ confidence }: { confidence: MatchConfidence }) {
  if (confidence === 'confirmed') {
    return (
      <Tooltip content="Linked by a shared identifier such as email, phone or the same platform account.">
        <Badge tone="success">Returning customer</Badge>
      </Tooltip>
    );
  }
  if (confidence === 'potential') {
    return (
      <Tooltip content="Possibly an existing customer. Not linked until someone on the team confirms.">
        <Badge tone="warning">Possible match</Badge>
      </Tooltip>
    );
  }
  return null;
}

export function DemoBadge() {
  return (
    <Tooltip content="Simulated data for exploring Knownlead. Not from a real account.">
      <Badge tone="warning">
        <FlaskConical />
        Demo
      </Badge>
    </Tooltip>
  );
}

// ---------- Page scaffolding ----------
export function PageHeader({ title, description, actions, className }: { title: string; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-3 border-b border-border px-4 py-4 sm:px-6', className)}>
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, className }: { icon: LucideIcon; title: string; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <div className="mb-3 flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-muted">
        <Icon className="size-5" />
      </div>
      <h3 className="text-sm font-semibold">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-[13px] text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry, className }: { message: string; onRetry?: () => void; className?: string }) {
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center px-6 py-10 text-center', className)}>
      <CircleAlert className="mb-2 size-5 text-danger" />
      <p className="text-[13px] text-muted">{message}</p>
      {onRetry && (
        <Button className="mt-3" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Callout({ tone = 'neutral', icon: Icon, title, children, className }: { tone?: 'neutral' | 'warning' | 'danger' | 'accent' | 'success'; icon?: LucideIcon; title?: string; children?: ReactNode; className?: string }) {
  const tones = {
    neutral: 'border-border bg-surface-2',
    warning: 'border-warning/30 bg-warning-soft',
    danger: 'border-danger/30 bg-danger-soft',
    accent: 'border-accent/30 bg-accent-soft',
    success: 'border-success/30 bg-success-soft',
  };
  const iconTones = { neutral: 'text-muted', warning: 'text-warning', danger: 'text-danger', accent: 'text-accent', success: 'text-success' };
  return (
    <div className={cn('flex gap-2.5 rounded-lg border px-3 py-2.5 text-[13px]', tones[tone], className)}>
      {Icon && <Icon className={cn('mt-0.5 size-4 shrink-0', iconTones[tone])} />}
      <div className="min-w-0 space-y-0.5">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className="text-muted">{children}</div>}
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('UI error', error, info.componentStack);
  }
  override render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <ErrorState message="This part of the page failed to load." onRetry={() => this.setState({ error: null })} className="h-full" />
        )
      );
    }
    return this.props.children;
  }
}
